/**
 * System-Status-Komponente für Admin-Dashboard
 * 
 * @module components/admin/SystemStatus
 */

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Loader2, Server, Database, HardDrive, RefreshCw, Trash2, Power, Wrench, AlertCircle, Cpu, GitBranch, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface SystemStatus {
  uptime: number;
  uptimeFormatted: string;
  memory: {
    heapUsed: number;
    heapTotal: number;
    rss: number;
    external?: number;
    heapUsedFormatted?: string;
    heapTotalFormatted?: string;
    rssFormatted?: string;
    heapUsagePercent?: number;
    systemTotal?: number;
    systemFree?: number;
    systemUsed?: number;
    systemUsagePercent?: number;
    ramUsagePercentOfLimit?: number;
    explanations?: {
      heapUsed?: string;
      heapTotal?: string;
      rss?: string;
      external?: string;
    };
  };
  cpu?: {
    user: number;
    system: number;
    userFormatted?: string;
    systemFormatted?: string;
    load1m?: number;
    load5m?: number;
    load1mFormatted?: string;
    load5mFormatted?: string;
    cores?: number;
  };
  disk?: {
    free: number;
    total: number;
    used: number;
    freeFormatted: string;
    totalFormatted: string;
    usedFormatted: string;
    usagePercent: number;
  };
  project?: {
    size: number;
    sizeFormatted: string;
    explanation?: string;
  };
  nodeVersion: string;
  platform: string;
  arch?: string;
  database: {
    status: string;
    error?: string;
    pool?: {
      totalCount: number;
      idleCount: number;
      waitingCount: number;
      usagePercent: number;
    };
  };
  redis?: {
    status: string;
    keyCount?: number;
    usedMemory?: string;
    error?: string;
  };
  deploy?: {
    version: string;
    commit?: string;
    branch?: string;
    buildTime?: string;
    environment: string;
  };
  container?: {
    restarts: number;
    isDocker: boolean;
  };
  timestamp: string;
}

export function SystemStatus() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isActionLoading, setIsActionLoading] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    action: string | null;
    endpoint: string | null;
    actionName: string | null;
    impact: string | null;
  }>({
    open: false,
    action: null,
    endpoint: null,
    actionName: null,
    impact: null,
  });

  const { data: status, isLoading, error } = useQuery<SystemStatus>({
    queryKey: ["/api/admin/status"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/status");
      if (!res.ok) throw new Error("Fehler beim Laden des System-Status");
      return await res.json();
    },
    refetchInterval: 30000, // Alle 30 Sekunden aktualisieren
    refetchIntervalInBackground: false, // Nicht pollen wenn Tab im Hintergrund
    gcTime: 60000, // Cache bleibt 1 Minute (kürzer für Live-Daten)
  });

  const handleAdminAction = async (action: string, endpoint: string, actionName: string, impact?: string) => {
    // Riskante Operationen erfordern Bestätigung
    const riskyActions = ['restart-app', 'rebuild-app', 'restart-db'];
    if (riskyActions.includes(action) && !confirmDialog.open) {
      setConfirmDialog({
        open: true,
        action,
        endpoint,
        actionName,
        impact: impact || "Diese Aktion kann die Anwendung kurzzeitig unverfügbar machen.",
      });
      return;
    }

    // Normale Aktionen oder bestätigte riskante Aktionen
    setIsActionLoading(action);
    setConfirmDialog({ open: false, action: null, endpoint: null, actionName: null, impact: null });
    
    try {
      const res = await apiRequest("POST", endpoint);
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast({
          title: "Erfolgreich",
          description: data.message || `${actionName} erfolgreich ausgeführt`,
        });
        // Status neu laden
        queryClient.invalidateQueries({ queryKey: ["/api/admin/status"] });
      } else {
        toast({
          title: "Fehler",
          description: data.error || data.message || `${actionName} fehlgeschlagen`,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: `${actionName} fehlgeschlagen: ${error instanceof Error ? error.message : String(error)}`,
        variant: "destructive",
      });
    } finally {
      setIsActionLoading(null);
    }
  };

  const executeConfirmedAction = () => {
    if (confirmDialog.action && confirmDialog.endpoint && confirmDialog.actionName) {
      handleAdminAction(confirmDialog.action, confirmDialog.endpoint, confirmDialog.actionName);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
        <p className="mt-2 text-gray-500">Lade System-Status...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Fehler beim Laden des System-Status</p>
      </div>
    );
  }

  if (!status) return null;

  return (
    <div className="space-y-6">
      {/* Deploy/Build-Info */}
      {status.deploy && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <GitBranch className="h-6 w-6 text-primary mr-2" />
            <h3 className="text-lg font-semibold">Deploy/Build-Info</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-500">Version</p>
              <p className="text-xl font-semibold">{status.deploy.version}</p>
            </div>
            {status.deploy.commit && (
              <div>
                <p className="text-sm text-gray-500">Commit</p>
                <p className="text-lg font-mono font-semibold">{status.deploy.commit}</p>
              </div>
            )}
            {status.deploy.branch && (
              <div>
                <p className="text-sm text-gray-500">Branch</p>
                <p className="text-lg font-semibold">{status.deploy.branch}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-500">Environment</p>
              <p className="text-lg font-semibold">
                <span className={`px-2 py-1 rounded text-xs ${
                  status.deploy.environment === 'production' ? 'bg-red-100 text-red-800' :
                  status.deploy.environment === 'staging' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                  {status.deploy.environment}
                </span>
              </p>
            </div>
            {status.deploy.buildTime && (
              <div className="md:col-span-2 lg:col-span-4">
                <p className="text-sm text-gray-500">Build-Zeit</p>
                <p className="text-sm font-medium">
                  {new Date(status.deploy.buildTime).toLocaleString('de-DE')}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Server-Status */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center mb-4">
          <Server className="h-6 w-6 text-primary mr-2" />
          <h3 className="text-lg font-semibold">Server-Status</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">Uptime</p>
            <p className="text-xl font-semibold">{status.uptimeFormatted}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Node.js Version</p>
            <p className="text-xl font-semibold">{status.nodeVersion}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Plattform</p>
            <p className="text-xl font-semibold">{status.platform}</p>
          </div>
          {status.container && (
            <div>
              <p className="text-sm text-gray-500">Container Restarts</p>
              <p className="text-xl font-semibold">{status.container.restarts}</p>
            </div>
          )}
          <div>
            <p className="text-sm text-gray-500">Letzte Aktualisierung</p>
            <p className="text-sm font-medium">
              {new Date(status.timestamp).toLocaleString('de-DE')}
            </p>
          </div>
        </div>
      </div>

      {/* Runtime-Metriken: CPU */}
      {status.cpu && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <Cpu className="h-6 w-6 text-primary mr-2" />
            <h3 className="text-lg font-semibold">CPU-Auslastung</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {status.cpu.load1m !== undefined && (
              <div>
                <p className="text-sm text-gray-500">Load (1m)</p>
                <p className="text-xl font-semibold">{status.cpu.load1mFormatted || `${status.cpu.load1m}%`}</p>
              </div>
            )}
            {status.cpu.load5m !== undefined && (
              <div>
                <p className="text-sm text-gray-500">Load (5m)</p>
                <p className="text-xl font-semibold">{status.cpu.load5mFormatted || `${status.cpu.load5m}%`}</p>
              </div>
            )}
            {status.cpu.cores !== undefined && (
              <div>
                <p className="text-sm text-gray-500">CPU-Kerne</p>
                <p className="text-xl font-semibold">{status.cpu.cores}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-500">User/System</p>
              <p className="text-sm font-medium">
                {status.cpu.userFormatted} / {status.cpu.systemFormatted}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Runtime-Metriken: RAM */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Activity className="h-6 w-6 text-primary mr-2" />
            <h3 className="text-lg font-semibold">RAM-Auslastung</h3>
          </div>
          {/* P0 Fix: Warnung nur bei System-RAM, nicht bei Container-Limit/RSS */}
          {status.memory.systemUsagePercent !== undefined && status.memory.systemUsagePercent > 85 && (
            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">
              ⚠️ System kritisch
            </span>
          )}
          {status.memory.systemUsagePercent !== undefined && status.memory.systemUsagePercent > 70 && status.memory.systemUsagePercent <= 85 && (
            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
              ⚠️ System hoch
            </span>
          )}
          {/* Separate Warnung für Heap, wenn sehr hoch */}
          {status.memory.heapUsagePercent !== undefined && status.memory.heapUsagePercent > 85 && (
            <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded ml-2">
              ⚠️ Heap hoch
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* System-RAM (wichtigste Metrik) */}
          {status.memory.systemTotal !== undefined && (
            <>
              <div>
                <p className="text-sm text-gray-500">System-RAM (gesamt)</p>
                <p className="text-lg font-semibold">
                  {Math.round(status.memory.systemTotal / 1024)} GB
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">System-RAM (frei)</p>
                <p className="text-lg font-semibold">
                  {status.memory.systemFree ? `${(status.memory.systemFree / 1024).toFixed(1)} GB` : '—'}
                </p>
                {status.memory.systemUsagePercent !== undefined && (
                  <div className="mt-1">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          status.memory.systemUsagePercent > 85 ? 'bg-red-500' :
                          status.memory.systemUsagePercent > 70 ? 'bg-yellow-500' :
                          'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(status.memory.systemUsagePercent, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{status.memory.systemUsagePercent.toFixed(1)}% verwendet</p>
                  </div>
                )}
              </div>
            </>
          )}
          {/* Node.js Heap */}
          <div>
            <p className="text-sm text-gray-500">Node.js Heap</p>
            <p className="text-lg font-semibold">
              {status.memory.heapUsedFormatted || `${Math.round(status.memory.heapUsed / 1024 / 1024)} MB`}
            </p>
            {status.memory.heapUsagePercent !== undefined && (
              <div className="mt-1">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      status.memory.heapUsagePercent > 85 ? 'bg-orange-500' :
                      status.memory.heapUsagePercent > 70 ? 'bg-yellow-500' :
                      'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(status.memory.heapUsagePercent, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">{status.memory.heapUsagePercent.toFixed(1)}% vom Heap</p>
              </div>
            )}
          </div>
          {/* RSS (Container-Limit, falls vorhanden) */}
          <div>
            <p className="text-sm text-gray-500">
              RSS {status.memory.ramUsagePercentOfLimit !== undefined ? '(Container)' : ''}
            </p>
            <p className="text-lg font-semibold">
              {status.memory.rssFormatted || `${Math.round(status.memory.rss / 1024 / 1024)} MB`}
            </p>
            {status.memory.ramUsagePercentOfLimit !== undefined && (
              <div className="mt-1">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      status.memory.ramUsagePercentOfLimit > 80 ? 'bg-orange-500' :
                      status.memory.ramUsagePercentOfLimit > 60 ? 'bg-yellow-500' :
                      'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(status.memory.ramUsagePercentOfLimit, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">{status.memory.ramUsagePercentOfLimit.toFixed(1)}% vom Container-Limit</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Disk-Informationen */}
      {status.disk && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <HardDrive className="h-6 w-6 text-primary mr-2" />
              <h3 className="text-lg font-semibold">Disk-Speicher</h3>
            </div>
            {status.disk.usagePercent > 80 && (
              <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">
                ⚠️ Kritisch
              </span>
            )}
            {status.disk.usagePercent > 60 && status.disk.usagePercent <= 80 && (
              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
                ⚠️ Hoch
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-500">Verwendet</p>
              <p className="text-lg font-semibold">{status.disk.usedFormatted}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Frei</p>
              <p className="text-lg font-semibold">{status.disk.freeFormatted}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Gesamt</p>
              <p className="text-lg font-semibold">{status.disk.totalFormatted}</p>
              {status.disk.usagePercent !== undefined && (
                <div className="mt-1">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        status.disk.usagePercent > 80 ? 'bg-red-500' :
                        status.disk.usagePercent > 60 ? 'bg-yellow-500' :
                        'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(status.disk.usagePercent, 100)}%` }}
                    />
                  </div>
                <p className="text-xs text-gray-500 mt-1">{status.disk.usagePercent.toFixed(1)}%</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Projektspeicher */}
      {status.project && status.project.sizeFormatted !== "—" && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <HardDrive className="h-6 w-6 text-primary mr-2" />
            <h3 className="text-lg font-semibold">Projektspeicher</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Projektgröße</span>
              <span className="text-xl font-semibold text-neutral-darker">
                {status.project.sizeFormatted}
              </span>
            </div>
            {status.project.explanation && (
              <p className="text-xs text-gray-500 mt-2">
                {status.project.explanation}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Datenbank-Status mit Operationen */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Database className="h-6 w-6 text-primary mr-2" />
            <h3 className="text-lg font-semibold">Datenbank-Status</h3>
          </div>
        </div>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  status.database.status === 'connected'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {status.database.status === 'connected' ? '✓ Verbunden' : '✗ Fehler'}
              </span>
              {status.database.error && (
                <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {status.database.error}
                </p>
              )}
              {status.database.pool && (
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Pool: Total</p>
                    <p className="text-sm font-semibold">{status.database.pool.totalCount}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Pool: Idle</p>
                    <p className="text-sm font-semibold">{status.database.pool.idleCount}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Pool: Waiting</p>
                    <p className="text-sm font-semibold">{status.database.pool.waitingCount}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Pool: Auslastung</p>
                    <p className="text-sm font-semibold">{status.database.pool.usagePercent}%</p>
                    <div className="mt-1">
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full ${
                            status.database.pool.usagePercent > 80 ? 'bg-red-500' :
                            status.database.pool.usagePercent > 60 ? 'bg-yellow-500' :
                            'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(status.database.pool.usagePercent, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleAdminAction('restart-db', '/api/admin/actions/restart-db', 'DB-Neustart', 'Die Datenbank wird für 10-20 Sekunden nicht verfügbar sein. Alle Datenbankabfragen werden fehlschlagen.')}
              disabled={isActionLoading === 'restart-db'}
              className="flex items-center gap-2 ml-4"
            >
              {isActionLoading === 'restart-db' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Power className="h-4 w-4" />
              )}
              DB Neustarten
            </Button>
          </div>
        </div>
      </div>

      {/* Redis-Status */}
      {status.redis && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6">
          <div className="flex items-center mb-4">
            <Activity className="h-5 w-5 md:h-6 md:w-6 text-red-500 mr-2" />
            <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white">Redis Cache</h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span
                className={`px-3 py-1 rounded-full text-xs md:text-sm font-medium ${
                  status.redis.status === 'connected'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                }`}
              >
                {status.redis.status === 'connected' ? '✓ Verbunden' : '✗ ' + (status.redis.error || 'Fehler')}
              </span>
            </div>
            {status.redis.status === 'connected' && (
              <div className="grid grid-cols-2 gap-3 md:gap-4 mt-3">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Cache Keys</p>
                  <p className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">
                    {status.redis.keyCount?.toLocaleString() || 0}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Speicherverbrauch</p>
                  <p className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">
                    {status.redis.usedMemory || 'N/A'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Admin-Operationen */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center mb-4">
          <Wrench className="h-6 w-6 text-primary mr-2" />
          <h3 className="text-lg font-semibold">System-Operationen</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Button
            variant="outline"
            onClick={() => handleAdminAction('clear-cache', '/api/admin/actions/clear-cache', 'Cache leeren')}
            disabled={isActionLoading === 'clear-cache'}
            className="flex items-center justify-center gap-2"
          >
            {isActionLoading === 'clear-cache' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            Cache leeren
          </Button>
          
          <Button
            variant="outline"
            onClick={() => handleAdminAction('restart-app', '/api/admin/actions/restart-app', 'App-Neustart', 'Die Anwendung wird für 10-30 Sekunden nicht verfügbar sein.')}
            disabled={isActionLoading === 'restart-app'}
            className="flex items-center justify-center gap-2"
          >
            {isActionLoading === 'restart-app' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            App Neustarten
          </Button>
          
          <Button
            variant="outline"
            onClick={() => handleAdminAction('rebuild-app', '/api/admin/actions/rebuild-app', 'App-Rebuild', 'Die Anwendung wird für 2-5 Minuten nicht verfügbar sein. Alle aktiven Verbindungen werden getrennt.')}
            disabled={isActionLoading === 'rebuild-app'}
            className="flex items-center justify-center gap-2"
          >
            {isActionLoading === 'rebuild-app' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Wrench className="h-4 w-4" />
            )}
            App Rebuilden
          </Button>
          
          <Button
            variant="outline"
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ["/api/admin/status"] });
              toast({
                title: "Status aktualisiert",
                description: "System-Status wird neu geladen",
              });
            }}
            className="flex items-center justify-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Status aktualisieren
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-4">
          ⚠️ Vorsicht: Neustart- und Rebuild-Operationen können die Anwendung kurzzeitig unverfügbar machen.
        </p>
      </div>

      {/* Confirmation-Dialog für riskante Operationen */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              Bestätigung erforderlich
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p className="font-medium">Möchten Sie wirklich "{confirmDialog.actionName}" ausführen?</p>
              {confirmDialog.impact && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm font-medium text-yellow-800">⚠️ Auswirkung:</p>
                  <p className="text-sm text-yellow-700 mt-1">{confirmDialog.impact}</p>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Diese Aktion kann nicht rückgängig gemacht werden.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmDialog({ open: false, action: null, endpoint: null, actionName: null, impact: null })}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={executeConfirmedAction}
              className="bg-red-600 hover:bg-red-700"
            >
              {confirmDialog.actionName} ausführen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

